/**
 * 商談を手動作成するダイアログ。
 * 顧客詳細「商談」タブの「+ 新規商談」ボタンから開く。
 *
 * createDeal Callable を呼び出して新規 Deal を作成する。
 */
import type { ContactKind, DealCategory, DealStage } from '@goenhub/shared';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useCreateDeal } from '@/hooks/useDeals';
import { useProductCategories } from '@/hooks/useProductCategories';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  contactKind: ContactKind | undefined;
  defaultTitle?: string;
}

const STAGE_OPTIONS: ReadonlyArray<{
  stage: DealStage;
  category: DealCategory;
  label: string;
}> = [
  { stage: 'document_requested', category: 'document_download', label: '資料請求 (document_requested)' },
  { stage: 'briefing', category: 'explanation', label: '個別説明 (briefing)' },
  { stage: 'quote', category: 'quote', label: '見積 (quote)' },
];

export function CreateDealDialog({
  open,
  onOpenChange,
  contactId,
  contactKind,
  defaultTitle,
}: Props) {
  const create = useCreateDeal();
  const categoriesQuery = useProductCategories();

  const [title, setTitle] = useState('');
  const [stage, setStage] = useState<DealStage>('briefing');
  // contact が unknown のときはユーザーに選ばせる。確定済なら disabled。
  const [pickedKind, setPickedKind] = useState<'btob' | 'btoc'>('btoc');
  const [primaryCategoryId, setPrimaryCategoryId] = useState<string>('');
  const [expectedAmount, setExpectedAmount] = useState<string>('');
  const [memo, setMemo] = useState<string>('');

  useEffect(() => {
    if (open) {
      setTitle(defaultTitle ?? '');
      setStage('briefing');
      setPickedKind(
        contactKind === 'btob' || contactKind === 'btoc' ? contactKind : 'btoc',
      );
      setPrimaryCategoryId('');
      setExpectedAmount('');
      setMemo('');
    }
  }, [open, contactKind, defaultTitle]);

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('タイトルは必須です');
      return;
    }
    const amount = expectedAmount.trim()
      ? Number(expectedAmount.replace(/[,¥\s]/g, ''))
      : null;
    if (amount !== null && (!Number.isFinite(amount) || amount < 0)) {
      toast.error('期待金額は0以上の数値で入力してください');
      return;
    }
    const opt = STAGE_OPTIONS.find((s) => s.stage === stage)!;
    try {
      await create.mutateAsync({
        contactId,
        contactKind: pickedKind,
        title: trimmed,
        category: opt.category,
        stage: opt.stage,
        primaryCategoryId: primaryCategoryId || null,
        expectedAmount: amount,
        memo: memo.trim() || undefined,
      });
      toast.success('商談を作成しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  const kindLocked = contactKind === 'btob' || contactKind === 'btoc';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>商談を手動作成</DialogTitle>
          <DialogDescription>
            この顧客に紐付く商談を手動で起票します。フォーム送信経由ではなく、
            電話・面談などのオフライン経路で得たリードを記録する用途です。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="cd-title">タイトル *</Label>
            <Input
              id="cd-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: ○○様 / 機材A 見積依頼"
            />
          </div>

          <div>
            <Label htmlFor="cd-stage">ステージ</Label>
            <select
              id="cd-stage"
              value={stage}
              onChange={(e) => setStage(e.target.value as DealStage)}
              className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
            >
              {STAGE_OPTIONS.map((opt) => (
                <option key={opt.stage} value={opt.stage}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs">顧客タイプ</Label>
            <div className="flex gap-2 mt-1">
              {(['btob', 'btoc'] as const).map((k) => (
                <label
                  key={k}
                  className={`flex-1 cursor-pointer text-center text-xs py-2 border rounded-sm transition-colors ${
                    pickedKind === k
                      ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                      : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                  } ${kindLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    name="cd-kind"
                    value={k}
                    checked={pickedKind === k}
                    onChange={() => setPickedKind(k)}
                    disabled={kindLocked}
                    className="sr-only"
                  />
                  {k === 'btob' ? 'BtoB (法人)' : 'BtoC (個人)'}
                </label>
              ))}
            </div>
            {kindLocked && (
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                顧客プロフィールで {pickedKind === 'btob' ? 'BtoB' : 'BtoC'}{' '}
                確定済のため変更不可。
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="cd-category">主軸カテゴリ (任意)</Label>
            <select
              id="cd-category"
              value={primaryCategoryId}
              onChange={(e) => setPrimaryCategoryId(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
            >
              <option value="">未指定</option>
              {(categoriesQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              商談ボードのフィルタや見積書ロゴの判定に使用します。
            </p>
          </div>

          <div>
            <Label htmlFor="cd-amount">期待金額 (任意)</Label>
            <Input
              id="cd-amount"
              type="text"
              inputMode="numeric"
              value={expectedAmount}
              onChange={(e) => setExpectedAmount(e.target.value)}
              placeholder="例: 800000"
            />
          </div>

          <div>
            <Label htmlFor="cd-memo">メモ (任意)</Label>
            <Textarea
              id="cd-memo"
              rows={3}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="例: 電話問い合わせ。来週水曜までに見積必要。"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={create.isPending}
          >
            {create.isPending ? '作成中…' : '商談を作成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
